import React from "react";
import { usePublications } from "context/PublicationProvider";
import { useMaterialUIController } from "context";
import MDBox from "components/MDBox";
import dayjs from "dayjs";
import Tooltip from "@mui/material/Tooltip";
import ClearIcon from "@mui/icons-material/Clear";

const StatusBadge = ({ value }) => {
  const normalized = String(value).trim().toLowerCase();
  const isYes = normalized === "yes";
  const isMaybe = normalized === "maybe";

  let backgroundColor = "#fdecec"; // default (No) - red
  let color = "#e34848";

  if (isYes) {
    backgroundColor = "#e6f7ec"; // green
    color = "#1e9e5a";
  } else if (isMaybe) {
    backgroundColor = "#e8f2ff"; // blue
    color = "#1A83FF";
  }

  return (
    <MDBox
      component="span"
      sx={{
        display: "inline-block",
        px: 1.25,
        py: 0.4,
        borderRadius: "12px",
        fontSize: "0.75rem",
        fontWeight: 600,
        backgroundColor,
        color,
      }}
    >
      {value}
    </MDBox>
  );
};

export default function Data() {
  const { rows, fetchPublications } = usePublications();
  const [controller] = useMaterialUIController();
  const { selection } = controller;

  const [transformedRows, setTransformedRows] = React.useState([]);

  const userRole = localStorage.getItem("userRole");
  const isPackage = selection === "package";

  // Fetch data on mount
  React.useEffect(() => {
    fetchPublications();
  }, []);

  console.log("rows", rows);


  // Transform/group rows only when rows or selection changes
  React.useEffect(() => {
    if (isPackage) {
      const groupedPackages = {};

      rows.forEach((row) => {
        if (row.packageName && row.packageName.trim().length > 0) {
          if (!groupedPackages[row.packageName]) {
            groupedPackages[row.packageName] = {
              ...row,
              // force sample from sampleUrls
              sample:
                Array.isArray(row.sampleUrls) &&
                  row.sampleUrls.length > 0
                  ? row.sampleUrls[0]
                  : "-",
            };
          }
        }
      });

      const groupedArray = Object.values(groupedPackages).map((pkg) => ({
        ...pkg,
        // IMPORTANT: override again to avoid null overwrite
        sample:
          Array.isArray(pkg.sampleUrls) &&
            pkg.sampleUrls.length > 0
            ? pkg.sampleUrls[0]
            : "-",
        publication: (
          <MDBox
            sx={{
              maxWidth: "120px",
              whiteSpace: "normal",
              wordBreak: "break-word",
              fontSize: "0.85rem",
            }}
          >
            {pkg.platformName || "-"}
          </MDBox>
        ),
      }));

      console.log("FINAL ROWS:", groupedArray); // 👈 check this

      setTransformedRows(groupedArray);
    } else {
      setTransformedRows(rows);
    }
  }, [rows, isPackage]);


  let columns = [
    ...(isPackage
      ? [
  {
  Header: (
   "Publication"
  ),
  accessor: "packageName",
  align: "left",
  sortable: false,
  Cell: ({ value }) => (
    <MDBox
      sx={{
        maxWidth: "170px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        fontSize: "0.85rem",
        marginLeft: "15px",
      }}
    >
      {value}
    </MDBox>
  ),
},
      ]
      : []),
  {
  Header: isPackage ? (
    "Platforms"
  ) : (
   "Publication"
  ),
  accessor: "publication",
  align: "left",
  sortable: false,
  width: isPackage ? 50 : 120,
  Cell: ({ value }) => (
    <MDBox
      sx={{
        maxWidth: "120px",
        whiteSpace: "normal",
        wordBreak: "break-word",
        fontSize: "0.85rem",
      }}
    >
      {value}
    </MDBox>
  ),
},
    // { Header: "Price", accessor: "gstPrice", align: "left", sortable: false },
    {
      Header: "Price",
      accessor: "gstPrice",
      align: "left",
      sortable: false,
      width: isPackage ? 40 : 100,        // set a fixed width in pixels
      Cell: ({ value }) => (
        <MDBox
          sx={{
            // width: "80px", 
            whiteSpace: "normal",
            wordBreak: "break-word",
            fontSize: "0.85rem",
          }}
        >
          {value}
        </MDBox>
      ),
    },
    // { Header: "$ USD Price", accessor: "usdPrice", align: "left", sortable: false },
    {
      Header: "$ USD Price",
      accessor: "usdPrice",
      align: "left",
      sortable: false,
      width: isPackage ? 40 : 100,        // set a fixed width in pixels
      Cell: ({ value }) => (
        <MDBox
          sx={{
            // width: "80px", 
            whiteSpace: "normal",
            wordBreak: "break-word",
            fontSize: "0.85rem",
          }}
        >
          {value}
        </MDBox>
      ),
    },
    ...(!isPackage
      ? [
        { Header: "Words Allowed", accessor: "wordsAllowed", align: "left", sortable: false },
        { Header: "No.of Backlinks", accessor: "backlinksAllowed", align: "left", sortable: false },
      ]
      : []),
    {
      Header: "TAT",
      accessor: "tat",
      align: "left",
      sortable: false,
      width: 40,        // set a fixed width in pixels
      Cell: ({ value }) => (
        <MDBox
          sx={{
            // width: "80px", 
            whiteSpace: "normal",
            wordBreak: "break-word",
            fontSize: "0.85rem",
          }}
        >
          {value}
        </MDBox>
      ),
    },
    // { Header: "Indexed", accessor: "index2", align: "center", sortable: false },
    // { Header: "DoFollow", accessor: "doFollow2", align: "center", sortable: false },
    ...(!isPackage
      ? [
        { Header: "Indexed", accessor: "index2", align: "center", sortable: false, Cell: ({ value }) => <StatusBadge value={value} /> },
        { Header: "DoFollow", accessor: "doFollow2", align: "center", sortable: false, Cell: ({ value }) => <StatusBadge value={value} /> },
      ]
      : []),
    {
      Header: "Genre",
      accessor: "genres",
      align: "left",
      sortable: false,
      width: isPackage ? 40 : "",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            maxWidth: isPackage ? "100px" : "70px",
            whiteSpace: "normal",
            wordBreak: "break-word",
            fontSize: "0.85rem",
          }}
        >
          {value}
        </MDBox>
      ),
    },
    ...(isPackage
      ? [
        // { Header: "Collab", accessor: "collab", align: "center", sortable: false },
        // { 
        //     Header: "Collab", 
        //     accessor: "collab", 
        //     align: "center", 
        //     sortable: false,
        //     Cell: ({value}) => <MDBox sx={{ width: "60px" }}>{value}</MDBox>
        // },
        {
          Header: "Collab",
          accessor: "collab",
          align: "left",
          sortable: false,
          width: 40,        // set a fixed width in pixels
        Cell: ({ value }) => <StatusBadge value={value} />
        },
        //         {
        //     Header: "Collab",
        //     accessor: "collab",
        //     align: "left",
        //     sortable: false,
        //       width: 40,          // 👈 column width fix
        // maxWidth: 40, 
        //     Cell: ({ value }) => (
        //       <MDBox
        //         sx={{
        //           maxWidth: "40px",
        //           whiteSpace: "normal",
        //           wordBreak: "break-word",
        //           fontSize: "0.85rem",
        //         }}
        //       >
        //         {value}
        //       </MDBox>
        //     ),
        //   },
        // { Header: "Sample", accessor: "sample", align: "center", sortable: false },
        {
          Header: "Sample",
          accessor: "sample",
          align: "center",
          width: isPackage ? 40 : '',
          sortable: false,
          Cell: ({ value }) => {
            // Agar URL nahi hai toh ClearIcon dikhaye
            if (!value || value === "-" || typeof value !== "string") {
              return (
                <Tooltip title="Sample unavailable" arrow placement="top">
                  <ClearIcon />
                </Tooltip>
              );
            }

            return (
              <MDBox
                component="a"
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "#1A73E8",
                  transition: "0.2s ease",
                  "&:hover": {
                    transform: "scale(1.2)",
                  },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </MDBox>
            );
          },
        }
      ]
      : []),
    // {
    //   Header: "Disclaimer",
    //   accessor: "disclaimer",
    //   align: "left",
    //   sortable: false,
    //   Cell: ({ value }) => (
    //     <MDBox
    //       sx={{
    //         maxWidth: "70px",
    //         whiteSpace: "normal",
    //         wordBreak: "break-word",
    //         fontSize: "0.85rem",
    //       }}
    //     >
    //       {value}
    //     </MDBox>
    //   ),
    // },
    ...(!isPackage
      ? [
        {
          Header: "Disclaimer",
          accessor: "disclaimer",
          align: "left",
          sortable: false,
          Cell: ({ value }) => (
            <MDBox
              sx={{
                maxWidth: "70px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                fontSize: "0.85rem",
              }}
            >
              {value}
            </MDBox>
          ),
        },
      ]
      : []),
  ];

  const dateDisplayFormat = (value) => {
    if (!value || value === "-") return "-";
    return dayjs(value).format("DD-MMM-YYYY"); // Yahan sirf Date dikhegi
  };
  // Find the index of Disclaimer column
  if (!isPackage) {
    const disclaimerIndex = columns.findIndex(col => col.accessor === "disclaimer");

    if (disclaimerIndex !== -1) {
      columns.splice(disclaimerIndex + 1, 0, {
        Header: "Region",
        accessor: "modeType",
        align: "center",
        sortable: false,
      });
    }
  }


  if (!isPackage) {
    columns.splice(11, 0, { Header: "Check DA", accessor: "daChecker", align: "center", sortable: false });
    columns.splice(12, 0, { Header: "Check Traffic", accessor: "trafficChecker", align: "center", sortable: false });
  }


  if (userRole === "SUPER_ADMIN") {
    columns.push(
      { Header: "Last Modified Name", accessor: "lastmodifiedBy", align: "center", sortable: false },
      { Header: "Created Date", accessor: "createdDate", align: "center", sortable: false },
      {
        Header: "Last Modified Date",
        accessor: "lastmodifiedDate",
        Cell: ({ value }) => dateDisplayFormat(value), // Display only date
        align: "center",
        sortable: false
      },
    );
  }

  // if (userRole === "CLIENT") {
  if (userRole === "CLIENT" || userRole === "BUSINESS_TEAM") {
    columns.push(
      // { Header: "Last Modified Date", accessor: "lastmodifiedDate", align: "center", sortable: false },
      {
        Header: "Last Modified Date",
        accessor: "lastmodifiedDate",
        Cell: ({ value }) => dateDisplayFormat(value), // Display only date
        align: "center",
        sortable: false
      },
    );
  }

  if (userRole === "SUPER_ADMIN" || userRole === "ADMIN") {
    columns.push({
      Header: "Action",
      accessor: "action",
      align: "center",
      sortable: false,
    });
  }

  return {
    columns,
    rows: transformedRows,
  };
}